import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Header, Button, Modal, Icon, Message,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import numeral from 'numeral';
import { isExpired, utcMoment, formatUtcDate } from '../../utilities/dateUtils';
import { formatNoteDate, newestFirst } from './PendingVraNotesDialog';
import {
  PENDING_VRA_NOTES_EMPTY_MESSAGE,
  PENDING_VRA_NOTES_LOADING_MESSAGE,
  PENDING_VRA_NOTES_SORT_LABEL,
} from '../../constants/pendingVraNotes';
import '../../assets/vraPendingStyle.css';

const formatCurrency = value => (value === null || value === undefined ? '' : `$ ${numeral(value).format('0,0')}`);
const formatDateTime = value => (value ? utcMoment(value).format('MM/DD/YYYY HH:mm') : '');

const VRAPendingDetails = props => {
  const {
    pendingVRA,
    isDetailsModalOpen,
    onCloseHandler,
    allowPendingVRA,
    invalidatePendingVRA,
    errorMessage,
    onErrorDismiss,
    notes,
    notesLoading,
    notesError,
    canEdit,
  } = props;

  const {
    id,
    odometerOnReturn,
    returnInitiatedAt,
    expirationDate,
    auctionLocation,
    returningUser,
    pendingReasons,
    guaranteePurchase,
  } = pendingVRA;

  const notesLoaded = !notesLoading && !notesError;
  const guaranteeOffer = guaranteePurchase?.guaranteeOffer;
  const vehicleInformation = guaranteeOffer?.vehicleInformation;
  const auctionInformation = guaranteeOffer?.auctionInformation;
  const guaranteeExpirationDate = guaranteePurchase?.expiresAt;

  const vin = vehicleInformation?.vin;
  const returnLocation = auctionLocation?.initials || auctionLocation?.name;
  const pendingReasonNotices = isEmpty(pendingReasons)
    ? []
    : pendingReasons.map(pendingReason => pendingReason.notice);

  return (
    <Modal
      open={isDetailsModalOpen}
      closeIcon
      onClose={onCloseHandler}
      closeOnEscape={false}
    >
      <Modal.Header>VRA Pending Details</Modal.Header>
      <Modal.Content image scrolling>
        <Modal.Description>
          {errorMessage && (
            <Message
              negative
              onDismiss={onErrorDismiss}
              content={errorMessage}
            />
          )}
          <Grid>
            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>VRA:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{id}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>VIN:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{vin}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Price:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatCurrency(guaranteeOffer?.guaranteePrice)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Vehicle Purchase Price:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatCurrency(auctionInformation?.vehiclePurchasePrice)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Vehicle:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {vehicleInformation?.year} {vehicleInformation?.make} {vehicleInformation?.model},
                {' '}{vehicleInformation?.odometerReading} miles, VIN {vin}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Pending Reason:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{pendingReasonNotices.join(', ')}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Dealer Name:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{guaranteeOffer?.dealerName}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Dealer Number:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{auctionInformation?.dealerNumber}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Location:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{returnLocation}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Odometer on Return:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{numeral(odometerOnReturn).format('0,0')}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Initiated At:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatDateTime(returnInitiatedAt)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Guarantee Expiration Date:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {isExpired(guaranteeExpirationDate) ? (
                  <span className='expiredText'>
                    {formatUtcDate(guaranteeExpirationDate)}
                    {' '}<Icon name='exclamation triangle' className='expiredIcon' />EXPIRED
                  </span>
                ) : (
                  formatUtcDate(guaranteeExpirationDate)
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Expiration Date:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {isExpired(expirationDate) ? (
                  <span className='expiredText'>
                    {formatUtcDate(expirationDate)}
                    {' '}<Icon name='exclamation triangle' className='expiredIcon' />EXPIRED
                  </span>
                ) : (
                  formatUtcDate(expirationDate)
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Initiated By:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {returningUser?.email && (
                  <a href={`mailto:${returningUser.email}`}>{returningUser.email}</a>
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <Header size='tiny' data-testid='pending-vra-details-notes-label'>
                  {notesLoaded ? `Notes (${notes.length})` : 'Notes'}
                  {notesLoaded && notes.length > 0 && (
                    <span className='detailNotesSort'>{` - ${PENDING_VRA_NOTES_SORT_LABEL}`}</span>
                  )}
                </Header>
              </Grid.Column>
              <Grid.Column width={12} data-testid='pending-vra-details-notes'>
                {notesLoading && <span data-testid='pending-vra-details-notes-loading'>{PENDING_VRA_NOTES_LOADING_MESSAGE}</span>}
                {!notesLoading && notesError && (
                  <span className='expiredText' data-testid='pending-vra-details-notes-error'>{notesError}</span>
                )}
                {!notesLoading && !notesError && notes.length === 0 && (
                  <span data-testid='pending-vra-details-notes-empty'>{PENDING_VRA_NOTES_EMPTY_MESSAGE}</span>
                )}
                {!notesLoading && !notesError && newestFirst(notes).map(entry => (
                  <div key={entry.id} className='detailNote' data-testid='pending-vra-details-note'>
                    <div className='detailNoteMeta'>
                      <span data-testid='pending-vra-details-note-author'>{entry.createdBy}</span>
                      {' — '}
                      <span data-testid='pending-vra-details-note-timestamp'>{formatNoteDate(entry.createdAt)}</span>
                    </div>
                    <div className='detailNoteText' data-testid='pending-vra-details-note-text'>{entry.note}</div>
                  </div>
                ))}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          color='red'
          onClick={invalidatePendingVRA}
          disabled={!canEdit}
          inverted
        >
          <Icon name='checkmark' /> Invalidate
        </Button>
        <Button
          color='green'
          onClick={allowPendingVRA}
          disabled={!canEdit}
          inverted
        >
          <Icon name='checkmark' /> Allow
        </Button>
        <Button onClick={onCloseHandler}>Cancel</Button>
      </Modal.Actions>
    </Modal>
  );
};

VRAPendingDetails.propTypes = {
  pendingVRA: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    odometerOnReturn: PropTypes.number,
    returnInitiatedAt: PropTypes.string,
    expirationDate: PropTypes.string,
    auctionLocation: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
    }),
    returningUser: PropTypes.shape({
      email: PropTypes.string,
    }),
    pendingReasons: PropTypes.arrayOf(PropTypes.shape({
      notice: PropTypes.string,
    })),
    guaranteePurchase: PropTypes.shape({
      expiresAt: PropTypes.string,
      guaranteeOffer: PropTypes.shape({
        dealerName: PropTypes.string,
        guaranteePrice: PropTypes.number,
        vehicleInformation: PropTypes.shape({
          vin: PropTypes.string,
          year: PropTypes.number,
          make: PropTypes.string,
          model: PropTypes.string,
          odometerReading: PropTypes.number,
        }),
        auctionInformation: PropTypes.shape({
          dealerNumber: PropTypes.number,
          vehiclePurchasePrice: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  isDetailsModalOpen: PropTypes.bool.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  allowPendingVRA: PropTypes.func.isRequired,
  invalidatePendingVRA: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  onErrorDismiss: PropTypes.func,
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    note: PropTypes.string,
    createdBy: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  notesLoading: PropTypes.bool,
  notesError: PropTypes.string,
  canEdit: PropTypes.bool,
};

VRAPendingDetails.defaultProps = {
  errorMessage: null,
  onErrorDismiss: undefined,
  notes: [],
  notesLoading: false,
  notesError: null,
  canEdit: true,
};

export default VRAPendingDetails;
